import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import BhtLogo from "@/components/common/BhtLogo";
import PreviewAccessModal from "@/components/preview/PreviewAccessModal";

const CONTACT_EMAIL = "info@bhtsolutions.nl";
const EASE = [0.16, 1, 0.3, 1];

/**
 * Premium "Launching Soon" page for BHT Solutions.
 *
 * Design vocabulary — Linear / Vercel / Stripe:
 * • Deep navy radial gradient canvas · 64px grid mask · gold radial glow ·
 *   four vertical shimmer lines · SVG turbulence grain · deep bottom vignette.
 * • Typography — Manrope (body) + Fraunces italic (single accent word in H1 +
 *   pillar titles + trust kicker). Font families are loaded in public/index.html.
 * • Motion — Framer Motion staggered fade-up on entry; shimmer lines respect
 *   prefers-reduced-motion.
 *
 * Content is fully bilingual (NL default, EN via /en route). SEO-friendly —
 * the page is publicly indexable to help hoteliers find the brand while the
 * full product page is in stealth.
 *
 * Hidden preview access: triple-click on the BHT logo opens the team-access
 * modal (existing PreviewAccessModal, server-validated). No visible UI.
 */
export default function ComingSoon({ onPreviewGranted }) {
  const { i18n } = useTranslation();
  const lang = i18n.language === "nl" ? "nl" : "en";
  const isNl = lang === "nl";
  const reduce = useReducedMotion();
  const [modalOpen, setModalOpen] = useState(false);
  const logoClicksRef = useRef(0);
  const logoTimerRef = useRef(null);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const switchLang = () => {
    const other = isNl ? "en" : "nl";
    const rest = window.location.pathname.replace(/^\/(en|nl)/, "");
    window.location.assign(`/${other}${rest || ""}`);
  };

  const handleLogoClick = () => {
    logoClicksRef.current += 1;
    if (logoTimerRef.current) clearTimeout(logoTimerRef.current);
    if (logoClicksRef.current >= 3) {
      logoClicksRef.current = 0;
      setModalOpen(true);
      return;
    }
    logoTimerRef.current = setTimeout(() => {
      logoClicksRef.current = 0;
    }, 1000);
  };

  // ---------- copy ----------
  const t = isNl
    ? {
        badge: "Binnenkort beschikbaar",
        h1_before: "De ",
        h1_accent: "toekomst",
        h1_after: " van hoteloperaties komt eraan.",
        body:
          "Wij bouwen aan een slimmer hospitality operating system waarin hotelprocessen, communicatie en digitale hulpmiddelen samenkomen in één krachtig platform.",
        sub: "Ons nieuwe platform is momenteel in ontwikkeling.",
        contactPrompt: "Een vraag of interesse in een samenwerking?",
        cta: "Neem contact op met BHT Solutions",
        trust: "Voor hotels die precisie, rust en gastgerichte techniek waarderen.",
        pillarsEyebrow: "Waar we aan bouwen",
        pillars: [
          {
            n: "01",
            title: "Operations",
            body:
              "Housekeeping, receptie, restaurant, onderhoud en marketing — samengebracht in één ritme, met duidelijke handovers en aantoonbare uitvoering.",
          },
          {
            n: "02",
            title: "Communicatie",
            body:
              "Interne communicatie, taken en kennis op één plek. Uw team wisselt niet meer tussen vier apps om één simpele vraag te beantwoorden.",
          },
          {
            n: "03",
            title: "Digitale tools",
            body:
              "Kennisbank, gastenapp, website, campagnes en inzichten — allemaal onder één merk, aangestuurd vanuit dezelfde werkomgeving.",
          },
        ],
        madeFor: "MADE FOR HOSPITALITY",
        copyright: (year) => `© ${year} BHT Solutions. Alle rechten voorbehouden.`,
        title: "BHT Solutions — Launching Soon",
        description:
          "Wij bouwen een slimmer hospitality operating system waarin operatie, communicatie en digitale hulpmiddelen samenkomen in één platform. Binnenkort beschikbaar.",
      }
    : {
        badge: "Launching soon",
        h1_before: "The ",
        h1_accent: "future",
        h1_after: " of hospitality operations is coming.",
        body:
          "We are building a smarter hospitality operating system that brings hotel operations, communication and digital tools together in one powerful platform.",
        sub: "Our new platform is currently in development.",
        contactPrompt: "Questions or interested in working with us?",
        cta: "Contact BHT Solutions",
        trust: "Built for hotels that value precision, calm and guest-first technology.",
        pillarsEyebrow: "What we are building",
        pillars: [
          {
            n: "01",
            title: "Operations",
            body:
              "Housekeeping, reception, restaurant, maintenance and marketing — brought into one rhythm, with clear handovers and demonstrable execution.",
          },
          {
            n: "02",
            title: "Communication",
            body:
              "Internal messaging, tasks and knowledge in one place. Your team no longer switches between four apps to answer a single question.",
          },
          {
            n: "03",
            title: "Digital tools",
            body:
              "Knowledge base, guest app, website, campaigns and insights — all under one brand, driven from the same workspace.",
          },
        ],
        madeFor: "MADE FOR HOSPITALITY",
        copyright: (year) => `© ${year} BHT Solutions. All rights reserved.`,
        title: "BHT Solutions — Launching Soon",
        description:
          "We are building a smarter hospitality operating system that brings operations, communication and digital tools together in one platform. Launching soon.",
      };

  const year = new Date().getFullYear();

  return (
    <div className="cs-root relative min-h-[100dvh] overflow-hidden bg-[#050505] text-white" data-testid="coming-soon">
      <Helmet>
        <html lang={lang} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        {/* SEO — publicly indexable for brand discoverability */}
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BHT Solutions" />
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={`https://bhtsolutions.nl/${lang}`} />
        <meta property="og:locale" content={isNl ? "nl_NL" : "en_US"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.title} />
        <meta name="twitter:description" content={t.description} />
        <link rel="canonical" href={`https://bhtsolutions.nl/${lang}`} />
        <link rel="alternate" hrefLang="nl" href="https://bhtsolutions.nl/nl" />
        <link rel="alternate" hrefLang="en" href="https://bhtsolutions.nl/en" />
        <link rel="alternate" hrefLang="x-default" href="https://bhtsolutions.nl/nl" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "BHT Solutions",
          url: "https://bhtsolutions.nl",
          email: `mailto:${CONTACT_EMAIL}`,
          description: t.description,
          sameAs: ["https://www.linkedin.com/company/bht-solutions"],
        })}</script>
        <style>{`
          .cs-root { font-family: 'Manrope', system-ui, -apple-system, sans-serif; font-weight: 300; }
          .cs-fraunces { font-family: 'Fraunces', 'Times New Roman', serif; }
          @keyframes cs-shimmer-line {
            0% { transform: translate3d(0, -80%, 0); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translate3d(0, 220%, 0); opacity: 0; }
          }
          @keyframes cs-ping-gold {
            0% { transform: scale(1); opacity: 0.9; }
            75%, 100% { transform: scale(2.4); opacity: 0; }
          }
        `}</style>
      </Helmet>

      {/* ─────────── Layer 1 · Radial navy → black gradient ─────────── */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            "radial-gradient(120% 90% at 50% 0%, #12234A 0%, #0A1024 42%, #050505 88%)",
        }}
      />

      {/* ─────────── Layer 2 · 64×64 grid with radial mask ─────────── */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 55% at 50% 30%, #000 40%, transparent 85%)",
          maskImage:
            "radial-gradient(ellipse 70% 55% at 50% 30%, #000 40%, transparent 85%)",
        }}
      />

      {/* ─────────── Layer 3 · Gold radial glow (top) ─────────── */}
      <div
        className="pointer-events-none absolute inset-x-0 top-[-25%] h-[70vh]"
        aria-hidden
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 20%, rgba(200,160,69,0.22), transparent 70%)",
          filter: "blur(70px)",
        }}
      />

      {/* ─────────── Layer 4 · Four vertical shimmer lines ─────────── */}
      {!reduce && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          {[
            { left: "12%", dur: 17, delay: 0, color: "rgba(200,160,69,0.55)" },
            { left: "34%", dur: 19, delay: 6, color: "rgba(0,212,255,0.35)" },
            { left: "63%", dur: 15, delay: 2, color: "rgba(200,160,69,0.4)" },
            { left: "86%", dur: 13, delay: 9, color: "rgba(229,195,106,0.45)" },
          ].map((s, i) => (
            <span
              key={i}
              className="absolute top-0 h-[220px] w-px"
              style={{
                left: s.left,
                background: `linear-gradient(to bottom, transparent, ${s.color} 40%, ${s.color} 60%, transparent)`,
                animation: `cs-shimmer-line ${s.dur}s linear ${s.delay}s infinite`,
              }}
            />
          ))}
        </div>
      )}

      {/* ─────────── Layer 5 · SVG turbulence grain ─────────── */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.045] mix-blend-overlay"
        aria-hidden
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* ─────────── Layer 6 · Deep bottom vignette ─────────── */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[35vh]"
        aria-hidden
        style={{ background: "linear-gradient(to bottom, transparent, rgba(5,5,5,0.9))" }}
      />

      {/* ═════════════════ CONTENT ═════════════════ */}

      {/* Top bar */}
      <header
        className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6 sm:px-8 sm:py-8"
        style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        data-testid="cs-topbar"
      >
        <button
          type="button"
          onClick={handleLogoClick}
          className="group flex items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#E5C36A]/70"
          aria-label="BHT Solutions"
          data-testid="cs-logo"
        >
          <div
            className="drop-shadow-[0_0_18px_rgba(200,160,69,0.35)] transition-transform duration-500 group-hover:scale-[1.03]"
          >
            <BhtLogo className="h-9 w-9 sm:h-10 sm:w-10" />
          </div>
          <span className="cs-fraunces hidden text-base tracking-tight text-white/90 sm:inline-block">
            BHT Solutions
          </span>
        </button>

        {/* NL / EN pill switch */}
        <div
          className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1 backdrop-blur-md"
          data-testid="cs-lang-switch"
        >
          {[
            { code: "nl", label: "NL" },
            { code: "en", label: "EN" },
          ].map(({ code, label }) => {
            const active = code === lang;
            return (
              <button
                key={code}
                type="button"
                onClick={() => (active ? null : switchLang())}
                className={
                  "min-h-[36px] rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors " +
                  (active
                    ? "bg-[#C8A045] text-[#0A1024]"
                    : "text-white/70 hover:text-white")
                }
                data-testid={`cs-lang-${code}`}
                aria-pressed={active}
                aria-label={code === "nl" ? "Nederlands" : "English"}
              >
                {label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Hero */}
      <main
        className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center px-5 pb-16 pt-8 sm:px-8 sm:pt-14 md:pt-20"
        data-testid="cs-hero"
      >
        {/* Big logo */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative"
        >
          <div
            className="drop-shadow-[0_0_50px_rgba(200,160,69,0.28)]"
            style={{ filter: "drop-shadow(0 12px 40px rgba(0,0,0,0.55))" }}
          >
            <BhtLogo className="h-28 w-28 sm:h-36 sm:w-36 md:h-40 md:w-40" />
          </div>
        </motion.div>

        {/* Status badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-[#C8A045]/40 bg-[#C8A045]/[0.07] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.25em] text-[#E5C36A] backdrop-blur-sm"
          data-testid="cs-badge"
        >
          <span className="relative flex h-2 w-2">
            <span
              className="absolute inline-flex h-full w-full rounded-full bg-[#E5C36A]"
              style={{ animation: reduce ? "none" : "cs-ping-gold 2.4s cubic-bezier(0, 0, 0.2, 1) infinite" }}
            />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E5C36A]" />
          </span>
          {t.badge}
        </motion.div>

        {/* H1 */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2, ease: EASE }}
          className="mt-8 max-w-3xl text-center text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl md:text-6xl lg:text-[64px]"
          data-testid="cs-h1"
        >
          {t.h1_before}
          <span className="cs-fraunces italic text-[#E5C36A]" style={{ fontWeight: 300 }}>
            {t.h1_accent}
          </span>
          {t.h1_after}
        </motion.h1>

        {/* Body paragraph */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.35, ease: EASE }}
          className="mt-8 max-w-xl text-center text-base leading-relaxed text-white/70 sm:text-lg"
          data-testid="cs-body"
        >
          {t.body}
        </motion.p>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="mt-4 text-center text-sm text-white/40"
          data-testid="cs-subtext"
        >
          {t.sub}
        </motion.p>

        {/* Gold divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1.1, delay: 0.6, ease: EASE }}
          className="mt-10 h-px w-24 origin-center"
          style={{
            background:
              "linear-gradient(to right, transparent, rgba(200,160,69,0.85), transparent)",
          }}
          aria-hidden
        />

        {/* Contact prompt */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
          className="mt-8 text-center text-sm text-white/65"
          data-testid="cs-contact-prompt"
        >
          {t.contactPrompt}
        </motion.p>

        {/* CTA */}
        <motion.a
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(isNl ? "Contact — BHT Solutions" : "Contact — BHT Solutions")}`}
          className="group mt-5 inline-flex min-h-[52px] items-center gap-2.5 rounded-full bg-[#C8A045] px-7 py-3.5 text-[13px] font-semibold tracking-[0.02em] text-[#0A1024] shadow-[0_20px_60px_-20px_rgba(200,160,69,0.55)] outline-none transition-all duration-300 hover:bg-[#D6B25A] hover:shadow-[0_25px_80px_-15px_rgba(200,160,69,0.7)] focus-visible:ring-2 focus-visible:ring-[#E5C36A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505] active:scale-[0.97]"
          data-testid="cs-cta"
        >
          <span>{t.cta}</span>
          <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </motion.a>

        {/* Trust tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.0, ease: EASE }}
          className="cs-fraunces mt-10 max-w-md text-center italic text-white/40"
          style={{ fontWeight: 300 }}
          data-testid="cs-trust"
        >
          {t.trust}
        </motion.p>

        {/* ─────── Pillars ─────── */}
        <section
          className="relative mt-24 w-full max-w-5xl"
          aria-labelledby="cs-pillars-heading"
          data-testid="cs-pillars"
        >
          <div className="mx-auto flex items-center justify-center gap-4">
            <span aria-hidden className="h-px w-16 bg-gradient-to-r from-transparent to-[#C8A045]/50" />
            <h2
              id="cs-pillars-heading"
              className="text-center text-[11px] font-medium uppercase tracking-[0.28em] text-[#E5C36A]"
              data-testid="cs-pillars-eyebrow"
            >
              {t.pillarsEyebrow}
            </h2>
            <span aria-hidden className="h-px w-16 bg-gradient-to-l from-transparent to-[#C8A045]/50" />
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {t.pillars.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1 + i * 0.08, ease: EASE }}
                className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-md transition-colors duration-500 hover:border-[#C8A045]/30 sm:p-7"
                data-testid={`cs-pillar-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="cs-fraunces text-sm tabular-nums text-[#E5C36A]/85"
                    style={{ fontWeight: 400 }}
                  >
                    {p.n}
                  </span>
                  <span
                    aria-hidden
                    className="h-px w-8 bg-[#C8A045]/50 transition-[width] duration-500 group-hover:w-14"
                  />
                </div>
                <h3
                  className="cs-fraunces mt-6 text-xl font-light tracking-tight text-white sm:text-2xl"
                  style={{ fontWeight: 400 }}
                >
                  {p.title}
                </h3>
                <p className="mt-3 text-[13.5px] leading-relaxed text-white/55">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer
        className="relative z-10 mt-16 border-t border-white/[0.06]"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
        data-testid="cs-footer"
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-3 px-5 py-8 sm:px-8">
          <p
            className="cs-fraunces text-[11px] italic uppercase tracking-[0.28em] text-[#E5C36A]/80"
            style={{ fontWeight: 400 }}
            data-testid="cs-made-for"
          >
            {t.madeFor}
          </p>
          <p className="text-center text-xs text-white/40" data-testid="cs-copyright">
            {t.copyright(year)}
          </p>
        </div>
      </footer>

      {/* Hidden team-access modal (triggered by 3× logo click) */}
      <PreviewAccessModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          onPreviewGranted?.();
        }}
      />
    </div>
  );
}
